const DRAFT_DIR = "draft-files";

/**
 * Chunk size for streamed OPFS writes. Writing in chunks lets a save report
 * byte-level progress per clip; smaller than this and the file is written in a
 * single call since the progress detail isn't worth the extra round-trips.
 */
const OPFS_WRITE_CHUNK_BYTES = 8 * 1024 * 1024;

function isOpfsSupported() {
  return (
    typeof navigator !== "undefined" &&
    "storage" in navigator &&
    "getDirectory" in navigator.storage
  );
}

function opfsNameForKey(key: string) {
  return encodeURIComponent(key);
}

async function getDraftDirectory() {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(DRAFT_DIR, { create: true });
}

export function canStoreDraftFilesInOpfs() {
  return isOpfsSupported();
}

export async function writeDraftFileToOpfs(
  key: string,
  file: File,
  onBytesWritten?: (bytesWritten: number) => void,
) {
  const dir = await getDraftDirectory();
  const name = opfsNameForKey(key);
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();

  try {
    if (!onBytesWritten || file.size <= OPFS_WRITE_CHUNK_BYTES) {
      await writable.write(file);
      onBytesWritten?.(file.size);
    } else {
      let offset = 0;
      while (offset < file.size) {
        const end = Math.min(offset + OPFS_WRITE_CHUNK_BYTES, file.size);
        await writable.write(file.slice(offset, end));
        offset = end;
        onBytesWritten(offset);
      }
    }
    await writable.close();
  } catch (error) {
    // A failed write (e.g. the source file was moved or changed on disk after
    // selection) leaves the stream errored. Closing an errored stream throws and
    // would mask the real cause, so abort it and surface the original error.
    await writable.abort().catch(() => {});
    throw error;
  }
}

export async function readDraftFileFromOpfs(key: string) {
  const dir = await getDraftDirectory();
  const handle = await dir.getFileHandle(opfsNameForKey(key));
  return handle.getFile();
}

export async function removeDraftFileFromOpfs(key: string) {
  try {
    const dir = await getDraftDirectory();
    await dir.removeEntry(opfsNameForKey(key));
  } catch {
    // Missing entries are fine.
  }
}

export async function clearDraftOpfsDirectory() {
  if (!isOpfsSupported()) return;

  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(DRAFT_DIR, { recursive: true });
  } catch {
    // Missing directory is fine.
  }
}

export async function removeDraftFilesFromOpfsExcept(keepKeys: Set<string>) {
  if (!isOpfsSupported()) return;

  const encodedKeepKeys = new Set([...keepKeys].map(opfsNameForKey));

  try {
    const dir = await getDraftDirectory();
    for await (const [name] of dir.entries()) {
      if (!encodedKeepKeys.has(name)) {
        await dir.removeEntry(name);
      }
    }
  } catch {
    // Best effort cleanup.
  }
}
